import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import * as XLSX from "xlsx";
import { loadRateData } from "@/data/rateLoader";
import { generateId, calculate, sumItems } from "@/lib/calculations";
import { quotesApi, adminApi } from "@/lib/api";
import { AmplifyLogo } from "@/components/AmplifyLogo";
import { useAuth } from "@/context/AuthContext";
import { useExportWorker } from "@/hooks/useExportWorker";
import { useQuoteDraftBackup } from "@/hooks/useQuoteDraftBackup";
import { 
  Header, SavedQuotesDrawer, NewQuoteConfirmDialog, UndoBanner, AdminPage, 
  LineItemForm, ProposalView, CurrentCostsTable, ProposedCostsTable, SavingsSummary,
  ClientInfoCard, RestoreDraftPrompt, UnsavedIndicator
} from "@/components/PricingEngine";
import {
  Calculator, Loader2
} from "lucide-react";

const flattenWcRates = (DEFAULT_WC_RATES, WC_DESCRIPTIONS, WC_RISK_LABELS) => {
  const arr = [];
  for (const st of Object.keys(DEFAULT_WC_RATES).sort()) {
    for (const [code, vals] of Object.entries(DEFAULT_WC_RATES[st])) {
      arr.push({
        state: st, classCode: code, billRate: vals[0], costRate: vals[1],
        modifier: vals[2], risk: WC_RISK_LABELS[vals[3]] || '',
        policyType: vals[4] ? 'MCP' : '', description: WC_DESCRIPTIONS[code] || ''
      });
    }
  }
  return arr;
};

export default function PricingEngine({ 
  onNavigateToDashboard, 
  loadQuoteId, 
  triggerNewQuote,
  onQuoteLoaded,
  onNewQuoteTriggered 
}) {
  // ── Auth ──
  const { user, isAdmin, maxDiscountPercent, discountLevel, logout } = useAuth();

  // ── Rate Data (loaded async) ──
  const [rateData, setRateData] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    loadRateData().then(data => {
      setRateData(data);
      setDataLoading(false);
    });
  }, []);

  // ── Page State ──
  const [page, setPage] = useState(1);

  // ── Core Quote State ──
  const [lineItems, setLineItems] = useState([]);
  const [form, setForm] = useState({ state: "IL", wcCode: "", wcRate: "", employeeCount: "", sutaRate: "", grossWages: "" });
  const [editingId, setEditingId] = useState(null);
  const [proposedRates, setProposedRates] = useState({});
  const [adminFeeType, setAdminFeeType] = useState("pepm");
  const [adminFeeValue, setAdminFeeValue] = useState("");
  const [proposedAdminFeeType, setProposedAdminFeeType] = useState("pepm");
  const [proposedAdminFeeValue, setProposedAdminFeeValue] = useState("");
  const [clientName, setClientName] = useState("");
  const [currentProvider, setCurrentProvider] = useState("");
  const [quoteDate, setQuoteDate] = useState(new Date().toISOString().split("T")[0]);
  const [preparedBy, setPreparedBy] = useState("");
  const quoteRef = useRef(null);

  // ── Set preparedBy from logged-in user ──
  useEffect(() => {
    if (user && !preparedBy) {
      setPreparedBy(user.full_name || "");
    }
  }, [user, preparedBy]);

  // ── UX State ──
  const [touched, setTouched] = useState({});
  const [undoItem, setUndoItem] = useState(null);
  const [undoProposed, setUndoProposed] = useState(null);
  const undoTimer = useRef(null);
  const [showNewQuoteConfirm, setShowNewQuoteConfirm] = useState(false);
  const [sutaAutoFill, setSutaAutoFill] = useState("");
  const [adpUnbundled, setAdpUnbundled] = useState(false); // When true, user enters per-line service fees
  const [serviceFees, setServiceFees] = useState({}); // Per-line service fees: { itemId: { rate: number } }
  const [duplicateWarning, setDuplicateWarning] = useState("");
  const [wcAutoFill, setWcAutoFill] = useState("");
  const [_discountWarnings, _setDiscountWarnings] = useState({}); // { itemId: { wc: bool, suta: bool } } - Reserved for future use
  const [editingSutaState, setEditingSutaState] = useState(null); // State code being edited for SUTA rate
  const [editingSutaValue, setEditingSutaValue] = useState(""); // Temp value while editing

  // ── Admin Tab State ──
  const [customSutaBases, setCustomSutaBases] = useState(null);
  const [wcRateTable, setWcRateTable] = useState([]);
  const [sutaUploadInfo, setSutaUploadInfo] = useState("");
  const [wcUploadInfo, setWcUploadInfo] = useState("");
  const [wcFilter, setWcFilter] = useState({ state: "", code: "" });
  const sutaFileRef = useRef(null);
  const wcFileRef = useRef(null);

  // ── SUTA Rate Table ──
  const [sutaRates, setSutaRates] = useState({});
  const [sutaRatesDraft, setSutaRatesDraft] = useState(null);
  const [sutaRatesSaved, setSutaRatesSaved] = useState("");

  // ── Export Worker (for PDF/Excel generation off main thread) ──
  const { generateExcel, generatePdf, generateWord, isExporting, exportProgress } = useExportWorker();

  // ── Initialize from rate data ──
  useEffect(() => {
    if (rateData) {
      setSutaRates(JSON.parse(JSON.stringify(rateData.DEFAULT_SUTA_RATES)));
      setWcRateTable(flattenWcRates(rateData.DEFAULT_WC_RATES, rateData.WC_DESCRIPTIONS, rateData.WC_RISK_LABELS));
    }
  }, [rateData]);

  // Derived data references (memoized to prevent unnecessary re-renders)
  const DEFAULT_SUTA_BASES = useMemo(() => rateData?.DEFAULT_SUTA_BASES || {}, [rateData]);
  const STATES = useMemo(() => rateData?.STATES || [], [rateData]);
  const MONOPOLISTIC_STATES = useMemo(() => rateData?.MONOPOLISTIC_STATES || [], [rateData]);
  const DEFAULT_SUTA_RATES = useMemo(() => rateData?.DEFAULT_SUTA_RATES || {}, [rateData]);
  const DEFAULT_WC_RATES = useMemo(() => rateData?.DEFAULT_WC_RATES || {}, [rateData]);
  const WC_DESCRIPTIONS = useMemo(() => rateData?.WC_DESCRIPTIONS || {}, [rateData]);
  const WC_RISK_LABELS = useMemo(() => rateData?.WC_RISK_LABELS || [], [rateData]);

  // ── Saved Quotes ──
  const [savedQuotes, setSavedQuotes] = useState([]);
  // Initialize currentQuoteId from localStorage if available
  const [currentQuoteId, setCurrentQuoteId] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('peo_currentQuoteId') || null;
    }
    return null;
  });
  const [showSavedQuotes, setShowSavedQuotes] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");

  // ── Draft Backup Restore Callback ──
  const handleRestoreDraft = useCallback((draft) => {
    setClientName(draft.clientName || "");
    setCurrentProvider(draft.currentProvider || "");
    setPreparedBy(draft.preparedBy || "");
    setQuoteDate(draft.quoteDate || "");
    setLineItems(draft.lineItems || []);
    setProposedRates(draft.proposedRates || {});
    setAdminFeeType(draft.adminFeeType || "pepm");
    setAdminFeeValue(draft.adminFeeValue || "");
    setProposedAdminFeeType(draft.proposedAdminFeeType || "pepm");
    setProposedAdminFeeValue(draft.proposedAdminFeeValue || "");
    setAdpUnbundled(draft.adpUnbundled || false);
    setServiceFees(draft.serviceFees || {});
    if (draft.currentQuoteId) {
      setCurrentQuoteId(draft.currentQuoteId);
    }
  }, []);

  // ── Draft Backup Hook ──
  const {
    hasUnsavedChanges,
    showRestorePrompt,
    savedDraft,
    restoreDraft,
    dismissRestorePrompt,
    markAsSaved,
  } = useQuoteDraftBackup({
    clientName,
    currentProvider,
    preparedBy,
    quoteDate,
    lineItems,
    proposedRates,
    adminFeeType,
    adminFeeValue,
    proposedAdminFeeType,
    proposedAdminFeeValue,
    adpUnbundled,
    serviceFees,
    currentQuoteId,
    onRestore: handleRestoreDraft,
  });

  // Persist currentQuoteId to localStorage whenever it changes
  useEffect(() => {
    if (currentQuoteId) {
      localStorage.setItem('peo_currentQuoteId', currentQuoteId);
    } else {
      localStorage.removeItem('peo_currentQuoteId');
    }
  }, [currentQuoteId]);

  // ── Derived Values ──
  const isClientState = useCallback((st) => sutaRates[st]?.bill === null, [sutaRates]);
  const getProposedSuta = useCallback((st, currentRate) => {
    const entry = sutaRates[st];
    if (!entry || entry.bill === null) return currentRate;
    return entry.bill;
  }, [sutaRates]);
  const isAdp = currentProvider === "ADP TotalSource";
  const sutaBases = customSutaBases || DEFAULT_SUTA_BASES;
  const EMPTY_FORM = { state: "IL", wcCode: "", wcRate: "", employeeCount: "", sutaRate: "", grossWages: "" };
  const REQUIRED = ["wcCode", "wcRate", "employeeCount", "sutaRate", "grossWages"];
  const fieldInvalid = (f) => touched[f] && !form[f];

  // ── Discount Calculation Helpers ──
  // Max discount based on user's discount level (standard=10%, manager=15%, vp=20%, admin=unlimited)
  const MAX_DISCOUNT_PERCENT = maxDiscountPercent;

  // ── Load admin overrides on mount ──
  useEffect(() => {
    const loadOverrides = async () => {
      try {
        const [sutaBasesRes, sutaRatesRes] = await Promise.all([
          adminApi.getOverride("suta_bases"),
          adminApi.getOverride("suta_rates"),
        ]);
        if (sutaBasesRes.data) setCustomSutaBases(sutaBasesRes.data);
        if (sutaRatesRes.data) setSutaRates(sutaRatesRes.data);
      } catch (error) { 
        console.warn('Failed to load admin overrides, using defaults:', error.message);
      }
    };
    loadOverrides();
  }, []);

  // ── Load saved quotes list ──
  const loadQuotes = useCallback(async () => {
    try {
      const q = await quotesApi.list();
      setSavedQuotes(q);
    } catch (error) { 
      console.error('Failed to load quotes list:', error.message);
    }
  }, []);
  useEffect(() => { loadQuotes(); }, [loadQuotes]);

  // ── Auto-load last saved quote on mount ──
  const [hasAutoLoaded, setHasAutoLoaded] = useState(false);
  useEffect(() => {
    const autoLoadQuote = async () => {
      // Only auto-load once, and only if there's a saved quote ID and no external loadQuoteId
      if (!hasAutoLoaded && currentQuoteId && !loadQuoteId) {
        setHasAutoLoaded(true);
        try {
          const q = await quotesApi.get(currentQuoteId);
          if (q) {
            setClientName(q.clientName || "");
            setCurrentProvider(q.currentProvider || "");
            setPreparedBy(q.preparedBy || "");
            setQuoteDate(q.quoteDate || "");
            setLineItems(q.lineItems || []);
            setProposedRates(q.proposedRates || {});
            setAdminFeeType(q.adminFeeType || "pepm");
            setAdminFeeValue(q.adminFeeValue || "");
            setProposedAdminFeeType(q.proposedAdminFeeType || "pepm");
            setProposedAdminFeeValue(q.proposedAdminFeeValue || "");
            if (q.adpUnbundled !== undefined) {
              setAdpUnbundled(q.adpUnbundled);
            } else if (q.adpBundled !== undefined) {
              setAdpUnbundled(!q.adpBundled);
            } else {
              setAdpUnbundled(false);
            }
            setServiceFees(q.serviceFees || {});
          }
        } catch (error) {
          // Quote may have been deleted, clear the stored ID
          console.warn('Failed to auto-load quote, clearing stored ID:', error.message);
          localStorage.removeItem('peo_currentQuoteId');
          setCurrentQuoteId(null);
        }
      }
    };
    autoLoadQuote();
  }, [currentQuoteId, loadQuoteId, hasAutoLoaded]);

  // ── Handle external quote load request ──
  useEffect(() => {
    if (loadQuoteId) {
      loadQuote(loadQuoteId);
      if (onQuoteLoaded) onQuoteLoaded();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadQuoteId]);

  // ── Handle external new quote request ──
  useEffect(() => {
    if (triggerNewQuote) {
      newQuote();
      if (onNewQuoteTriggered) onNewQuoteTriggered();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerNewQuote]);

  // ── Ctrl+Enter shortcut ──
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        if (page === 1 && lineItems.length > 0) setPage(2);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [page, lineItems.length]);

  // ── WC Rate Lookup ──
  const wcIsCustom = useRef(false);
  const lookupWcEntry = useCallback((state, code) => {
    if (!code) return null;
    const trimmed = code.trim();
    const stData = DEFAULT_WC_RATES[state];
    if (stData && stData[trimmed]) {
      const v = stData[trimmed];
      return { state, classCode: trimmed, billRate: v[0], costRate: v[1], modifier: v[2], risk: WC_RISK_LABELS[v[3]] || '', policyType: v[4] ? 'MCP' : '', description: WC_DESCRIPTIONS[trimmed] || '' };
    }
    if (wcIsCustom.current) {
      const found = wcRateTable.find(r => r.state === state && r.classCode === trimmed);
      return found || null;
    }
    return null;
  }, [DEFAULT_WC_RATES, WC_DESCRIPTIONS, WC_RISK_LABELS, wcRateTable]);

  const lookupWcRate = useCallback((state, code) => {
    const entry = lookupWcEntry(state, code);
    return entry ? entry.billRate : null;
  }, [lookupWcEntry]);

  // ── Discount Calculation Helpers (must be after lookupWcRate) ──
  // Calculate discount percentage from standard rate
  const calcDiscountPct = useCallback((standardRate, proposedRate) => {
    if (!standardRate || standardRate === 0) return 0;
    const discount = ((standardRate - proposedRate) / standardRate) * 100;
    return Math.round(discount * 10) / 10; // Round to 1 decimal
  }, []);

  // Check if discount exceeds maximum allowed (reserved for future validation UI)
  const _isDiscountExceeded = useCallback((standardRate, proposedRate) => {
    if (isAdmin) return false; // Admins have no limit
    const discount = calcDiscountPct(standardRate, proposedRate);
    return discount > MAX_DISCOUNT_PERCENT;
  }, [isAdmin, calcDiscountPct, MAX_DISCOUNT_PERCENT]);

  // Get standard WC bill rate for a state/code
  const getStandardWcRate = useCallback((state, code) => {
    return lookupWcRate(state, code) ?? null;
  }, [lookupWcRate]);

  // Get WC COST rate for a state/code (for admin profit margin check)
  const getWcCostRate = useCallback((state, code) => {
    const entry = lookupWcEntry(state, code);
    return entry ? entry.costRate : null;
  }, [lookupWcEntry]);

  // Get standard SUTA bill rate for a state
  const getStandardSutaRate = useCallback((state) => {
    const entry = sutaRates[state];
    if (!entry || entry.bill === null) return null;
    return entry.bill;
  }, [sutaRates]);

  // Get SUTA COST rate for a state (for admin profit margin check)
  const getSutaCostRate = useCallback((state) => {
    const entry = sutaRates[state];
    if (!entry || entry.cost === null) return null;
    return entry.cost;
  }, [sutaRates]);

  // Update SUTA rate for ALL items in a given state (since SUTA is state-level, not per class code)
  const updateStateSutaRate = useCallback((state, newRate) => {
    const itemsInState = lineItems.filter(li => li.state === state);
    if (itemsInState.length === 0) return;
    
    setProposedRates(prev => {
      const updated = { ...prev };
      itemsInState.forEach(item => {
        updated[item.id] = { ...updated[item.id], sutaRate: newRate };
      });
      return updated;
    });
  }, [lineItems]);

  // Update CURRENT SUTA rate for ALL items in a given state
  const updateCurrentStateSutaRate = useCallback((state, newRate) => {
    const rate = parseFloat(newRate);
    if (isNaN(rate) || rate < 0) return;
    
    setLineItems(prev => prev.map(item => 
      item.state === state ? { ...item, sutaRate: rate } : item
    ));
    
    // Also update proposed rates if they exist for client reporting states
    const isClientReporting = sutaRates[state]?.bill === null;
    if (isClientReporting) {
      setProposedRates(prev => {
        const updated = { ...prev };
        lineItems.filter(li => li.state === state).forEach(item => {
          if (updated[item.id]) {
            updated[item.id] = { ...updated[item.id], sutaRate: rate };
          }
        });
        return updated;
      });
    }
    
    setEditingSutaState(null);
    setEditingSutaValue("");
  }, [lineItems, sutaRates]);

  // Start editing SUTA rate for a state
  const startEditingSuta = useCallback((state, currentRate) => {
    setEditingSutaState(state);
    setEditingSutaValue(currentRate.toString());
  }, []);

  // Cancel editing SUTA rate
  const cancelEditingSuta = useCallback(() => {
    setEditingSutaState(null);
    setEditingSutaValue("");
  }, []);

  // ── Form Handling ──
  const updateForm = (field, value) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setDuplicateWarning("");
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === "state") {
        const entry = lookupWcEntry(value, prev.wcCode);
        if (entry) {
          next.wcRate = entry.billRate;
          setWcAutoFill(`WC bill rate ${entry.billRate}% applied for ${value}-${prev.wcCode}`);
          setTimeout(() => setWcAutoFill(""), 3000);
        }
        // Check if we already have line items for this state - use their SUTA rate
        const existingStateItem = lineItems.find(li => li.state === value);
        if (existingStateItem) {
          next.sutaRate = existingStateItem.sutaRate;
          setSutaAutoFill(`SUTA rate ${existingStateItem.sutaRate}% matched to existing ${value} items`);
          setTimeout(() => setSutaAutoFill(""), 3000);
        } else {
          // No existing items for this state, use default from rate table
          const stEntry = sutaRates[value];
          if (stEntry && stEntry.cost !== null) {
            next.sutaRate = stEntry.cost;
            setSutaAutoFill(`SUTA cost rate ${stEntry.cost}% applied for ${value}`);
            setTimeout(() => setSutaAutoFill(""), 3000);
          }
        }
      }
      if (field === "wcCode") {
        const entry = lookupWcEntry(prev.state, value);
        if (entry) {
          next.wcRate = entry.billRate;
          setWcAutoFill(`WC bill rate ${entry.billRate}% applied for ${prev.state}-${value}`);
          setTimeout(() => setWcAutoFill(""), 3000);
        }
      }
      return next;
    });
  };

  // ── Add Line Item ──
  const addLineItem = () => {
    const missing = REQUIRED.filter(f => !form[f]);
    if (missing.length > 0) {
      setTouched(prev => {
        const next = { ...prev };
        missing.forEach(f => { next[f] = true; });
        return next;
      });
      return;
    }
    
    // Check if there are existing items for this state - use their SUTA rate for consistency
    const existingStateItem = lineItems.find(li => li.state === form.state);
    const sutaForState = existingStateItem ? existingStateItem.sutaRate : parseFloat(form.sutaRate);
    
    const item = {
      id: generateId(),
      state: form.state,
      wcCode: form.wcCode.trim(),
      wcRate: parseFloat(form.wcRate),
      employeeCount: parseInt(form.employeeCount, 10),
      sutaRate: sutaForState, // Use consistent SUTA for the state
      grossWages: parseFloat(form.grossWages),
    };
    
    // Check for duplicate
    const dup = lineItems.find(li => li.state === item.state && li.wcCode === item.wcCode);
    if (dup) {
      setDuplicateWarning(`Duplicate: ${item.state} / ${item.wcCode} already exists. Added anyway.`);
      setTimeout(() => setDuplicateWarning(""), 4000);
    }
    
    // Notify user if SUTA was adjusted to match existing items
    if (existingStateItem && existingStateItem.sutaRate !== parseFloat(form.sutaRate)) {
      setSutaAutoFill(`SUTA rate matched to ${sutaForState}% for consistency with other ${form.state} items`);
      setTimeout(() => setSutaAutoFill(""), 4000);
    }
    
    if (editingId) {
      setLineItems(prev => prev.map(li => li.id === editingId ? { ...item, id: editingId } : li));
      // For monopolistic states, proposed WC rate = current WC rate
      const isMonopolistic = MONOPOLISTIC_STATES.includes(item.state);
      const newDefaultWc = isMonopolistic ? item.wcRate : (lookupWcRate(item.state, item.wcCode) ?? 1);
      // For client reporting states, proposed SUTA rate = current SUTA rate
      const isClientReporting = isClientState(item.state);
      const existingProposedSuta = existingStateItem ? proposedRates[existingStateItem.id]?.sutaRate : null;
      const newDefaultSuta = isClientReporting ? sutaForState : (existingProposedSuta ?? getProposedSuta(item.state, sutaForState));
      setProposedRates(prev => ({ ...prev, [editingId]: { wcRate: newDefaultWc, sutaRate: newDefaultSuta } }));
      setEditingId(null);
    } else {
      setLineItems(prev => [...prev, item]);
      // For monopolistic states, proposed WC rate = current WC rate
      const isMonopolistic = MONOPOLISTIC_STATES.includes(item.state);
      const defaultWc = isMonopolistic ? item.wcRate : (lookupWcRate(item.state, item.wcCode) ?? 1);
      // For client reporting states, proposed SUTA rate = current SUTA rate
      const isClientReporting = isClientState(item.state);
      const existingProposedSuta = existingStateItem ? proposedRates[existingStateItem.id]?.sutaRate : null;
      const defaultSuta = isClientReporting ? sutaForState : (existingProposedSuta ?? getProposedSuta(item.state, sutaForState));
      setProposedRates(prev => ({ ...prev, [item.id]: { wcRate: defaultWc, sutaRate: defaultSuta } }));
    }
    
    // Reset form but pre-fill SUTA if there are existing items for the default state (IL)
    // We need to check after adding the new item
    const allItemsAfterAdd = editingId ? lineItems : [...lineItems, item];
    const existingDefaultStateItem = allItemsAfterAdd.find(li => li.state === "IL");
    const newFormState = existingDefaultStateItem 
      ? { ...EMPTY_FORM, sutaRate: String(existingDefaultStateItem.sutaRate) }
      : EMPTY_FORM;
    
    setForm(newFormState);
    setTouched({});
    wcIsCustom.current = false;
  };

  // ── Delete Line Item ──
  const removeLineItem = (id) => {
    const removed = lineItems.find(li => li.id === id);
    if (!removed) return;
    setUndoItem(removed);
    setUndoProposed(proposedRates[id]);
    setLineItems(prev => prev.filter(li => li.id !== id));
    setProposedRates(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(() => { setUndoItem(null); setUndoProposed(null); }, 8000);
  };

  const undoDelete = () => {
    if (!undoItem) return;
    setLineItems(prev => [...prev, undoItem]);
    setProposedRates(prev => ({ ...prev, [undoItem.id]: undoProposed || { wcRate: lookupWcRate(undoItem.state, undoItem.wcCode) ?? 1, sutaRate: getProposedSuta(undoItem.state, undoItem.sutaRate) } }));
    setUndoItem(null);
    setUndoProposed(null);
    clearTimeout(undoTimer.current);
  };

  // ── Edit Line Item ──
  const startEditItem = (item) => {
    setEditingId(item.id);
    setForm({ state: item.state, wcCode: item.wcCode, wcRate: String(item.wcRate), employeeCount: String(item.employeeCount), sutaRate: String(item.sutaRate), grossWages: String(item.grossWages) });
    setTouched({});
  };

  // ── New Quote ──
  const newQuote = () => {
    setLineItems([]);
    setProposedRates({});
    setForm(EMPTY_FORM);
    setTouched({});
    setAdminFeeType("pepm");
    setAdminFeeValue("");
    setProposedAdminFeeType("pepm");
    setProposedAdminFeeValue("");
    setClientName("");
    setCurrentProvider("");
    setPreparedBy(user?.full_name || "");
    setQuoteDate(new Date().toISOString().split("T")[0]);
    setAdpUnbundled(false);
    setServiceFees({});
    setPage(1);
    setCurrentQuoteId(null);
    setShowNewQuoteConfirm(false);
  };

  // ── Save / Load Quote ──
  const saveQuote = async () => {
    // Prevent saving if discounts exceed limits (for non-admin users)
    if (hasExceededDiscounts) {
      setSaveStatus(`Max ${MAX_DISCOUNT_PERCENT}% discount exceeded`);
      setTimeout(() => setSaveStatus(""), 4000);
      return;
    }
    
    const data = {
      clientName, currentProvider, preparedBy, quoteDate, lineItems,
      proposedRates, adminFeeType, adminFeeValue,
      proposedAdminFeeType, proposedAdminFeeValue, adpUnbundled,
      serviceFees, // Per-line service fees for unbundled ADP
    };
    try {
      let res;
      if (currentQuoteId) {
        res = await quotesApi.update(currentQuoteId, data);
      } else {
        res = await quotesApi.create(data);
        setCurrentQuoteId(res.id);
      }
      setSaveStatus("Saved!");
      markAsSaved(); // Clear draft backup after successful save
      setTimeout(() => setSaveStatus(""), 3000);
      loadQuotes();
    } catch (error) {
      console.error('Failed to save quote:', error.message);
      setSaveStatus("Save failed");
      setTimeout(() => setSaveStatus(""), 3000);
    }
  };

  const loadQuote = async (id) => {
    try {
      const q = await quotesApi.get(id);
      setClientName(q.clientName || "");
      setCurrentProvider(q.currentProvider || "");
      setPreparedBy(q.preparedBy || "");
      setQuoteDate(q.quoteDate || "");
      setLineItems(q.lineItems || []);
      setProposedRates(q.proposedRates || {});
      setAdminFeeType(q.adminFeeType || "pepm");
      setAdminFeeValue(q.adminFeeValue || "");
      setProposedAdminFeeType(q.proposedAdminFeeType || "pepm");
      setProposedAdminFeeValue(q.proposedAdminFeeValue || "");
      // Support both old (adpBundled) and new (adpUnbundled) format
      // Old: adpBundled=true means bundled. New: adpUnbundled=true means unbundled
      if (q.adpUnbundled !== undefined) {
        setAdpUnbundled(q.adpUnbundled);
      } else if (q.adpBundled !== undefined) {
        // Migrate old format: adpBundled=true -> adpUnbundled=false (bundled is default now)
        setAdpUnbundled(!q.adpBundled);
      } else {
        setAdpUnbundled(false); // Default to bundled
      }
      setServiceFees(q.serviceFees || {});
      setCurrentQuoteId(q.id);
      setShowSavedQuotes(false);
      setPage(1);
    } catch (error) { 
      console.error('Failed to load quote:', error.message);
    }
  };

  const deleteQuote = async (id) => {
    try {
      await quotesApi.delete(id);
      if (currentQuoteId === id) setCurrentQuoteId(null);
      loadQuotes();
    } catch (error) { 
      console.error('Failed to delete quote:', error.message);
    }
  };

  // ── Save as New Version ──
  const saveAsNewVersion = async () => {
    if (!currentQuoteId) {
      // No existing quote, just save normally
      saveQuote();
      return;
    }
    
    if (hasExceededDiscounts) {
      setSaveStatus(`Max ${MAX_DISCOUNT_PERCENT}% discount exceeded`);
      setTimeout(() => setSaveStatus(""), 4000);
      return;
    }
    
    try {
      // Create a new version of the current quote
      const newQuote = await quotesApi.createNewVersion(currentQuoteId);
      
      // Update the new version with current data
      const data = {
        clientName, currentProvider, preparedBy, quoteDate, lineItems,
        proposedRates, adminFeeType, adminFeeValue,
        proposedAdminFeeType, proposedAdminFeeValue, adpUnbundled,
        serviceFees,
      };
      await quotesApi.update(newQuote.id, data);
      
      setCurrentQuoteId(newQuote.id);
      setSaveStatus(`Saved as v${newQuote.version}!`);
      setTimeout(() => setSaveStatus(""), 3000);
      loadQuotes();
    } catch (error) {
      console.error('Failed to save new version:', error.message);
      setSaveStatus("Version save failed");
      setTimeout(() => setSaveStatus(""), 3000);
    }
  };

  // ── Save and Next (to Proposal) ──
  const saveAndNext = async () => {
    await saveQuote();
    if (!hasExceededDiscounts && lineItems.length > 0) {
      setPage(2);
    }
  };

  // ── View Proposal (with validation and auto-save) ──
  const [showAdminFeeWarning, setShowAdminFeeWarning] = useState(false);
  
  const viewProposal = async () => {
    // Validate that proposed admin fee is entered
    if (!proposedAdminFeeValue || parseFloat(proposedAdminFeeValue) <= 0) {
      setShowAdminFeeWarning(true);
      // Also show alert for immediate feedback
      alert("Please enter a Proposed Admin Fee before viewing the proposal.");
      return;
    }
    
    setShowAdminFeeWarning(false);
    
    // Auto-save the quote before navigating
    await saveQuote();
    
    // Navigate to proposal page if no issues
    if (!hasExceededDiscounts && lineItems.length > 0) {
      setPage(2);
    }
  };

  // ── Print Proposal ──
  const printProposal = () => {
    window.print();
  };

  // ── Admin: SUTA Rates Editing ──
  const startEditSutaRates = () => { setSutaRatesDraft(JSON.parse(JSON.stringify(sutaRates))); setSutaRatesSaved(""); };
  const cancelEditSutaRates = () => { setSutaRatesDraft(null); setSutaRatesSaved(""); };
  const saveSutaRates = async () => {
    const newRates = JSON.parse(JSON.stringify(sutaRatesDraft));
    setSutaRates(newRates);
    setSutaRatesDraft(null);
    setSutaRatesSaved("Rate table saved");
    setTimeout(() => setSutaRatesSaved(""), 3000);
    try { 
      await adminApi.saveOverride("suta_rates", newRates); 
    } catch (error) { 
      console.error('Failed to persist SUTA rates override:', error.message);
    }
  };
  const updateDraftRate = (st, field, value) => {
    setSutaRatesDraft(prev => {
      const next = { ...prev };
      const raw = value.trim();
      if (raw === "" || raw.toUpperCase() === "CLIENT") {
        next[st] = { ...next[st], [field]: null };
      } else {
        const num = parseFloat(raw);
        if (!isNaN(num)) next[st] = { ...next[st], [field]: num };
      }
      return next;
    });
  };
  const resetSutaRatesToDefaults = async () => {
    setSutaRates(JSON.parse(JSON.stringify(DEFAULT_SUTA_RATES)));
    setSutaRatesDraft(null);
    setSutaRatesSaved("Reset to defaults");
    setTimeout(() => setSutaRatesSaved(""), 3000);
    try { 
      await adminApi.resetOverride("suta_rates"); 
    } catch (error) { 
      console.error('Failed to reset SUTA rates override:', error.message);
    }
  };

  // ── Admin: SUTA Bases Upload ──
  const handleSutaUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "array" });
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
        const bases = {};
        let count = 0;
        for (const row of rows) {
          const st = (row.State || row.state || "").toUpperCase().trim();
          const val = parseFloat(row.WageBase || row.wagebase || row["Wage Base"] || row.wage_base || 0);
          if (st && STATES.includes(st) && val > 0) { bases[st] = val; count++; }
        }
        if (count > 0) {
          setCustomSutaBases(bases);
          setSutaUploadInfo(`Loaded ${count} custom wage bases`);
          try { 
            await adminApi.saveOverride("suta_bases", bases); 
          } catch (error) { 
            console.error('Failed to persist SUTA bases override:', error.message);
          }
        } else {
          setSutaUploadInfo("No valid data found");
        }
        setTimeout(() => setSutaUploadInfo(""), 4000);
      } catch (err) {
        setSutaUploadInfo("Upload error: " + err.message);
        setTimeout(() => setSutaUploadInfo(""), 4000);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  // ── Admin: WC Rate Upload ──
  const handleWcUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "array" });
        const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
        const arr = rows.map(r => ({
          state: (r.State || r.state || "").toUpperCase().trim(),
          classCode: String(r.ClassCode || r.classcode || r["Class Code"] || r.code || "").trim(),
          billRate: parseFloat(r.BillRate || r.billrate || r["Bill Rate"] || r.bill || 0),
          costRate: parseFloat(r.CostRate || r.costrate || r["Cost Rate"] || r.cost || 0),
          modifier: parseFloat(r.Modifier || r.modifier || 1),
          risk: r.Risk || r.risk || "",
          policyType: r.PolicyType || r.policytype || "",
          description: r.Description || r.description || WC_DESCRIPTIONS[String(r.ClassCode || r.classcode || r["Class Code"] || r.code || "").trim()] || "",
        })).filter(r => r.state && r.classCode && r.billRate > 0);
        if (arr.length > 0) {
          setWcRateTable(arr);
          setWcUploadInfo(`Loaded ${arr.length} WC rates`);
          wcIsCustom.current = true;
        } else {
          setWcUploadInfo("No valid data found");
        }
        setTimeout(() => setWcUploadInfo(""), 4000);
      } catch (err) {
        setWcUploadInfo("Upload error: " + err.message);
        setTimeout(() => setWcUploadInfo(""), 4000);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = "";
  };

  // ── Calculation Helpers ──
  const getAdminFee = useCallback((feeType, feeValue, employees, grossWages) => {
    const v = parseFloat(feeValue) || 0;
    if (feeType === "pepm") return v * employees * 12;
    return grossWages * (v / 100);
  }, []);

  // ── Memoized Derived Values ──
  const proposedItems = useMemo(() => lineItems.map(item => ({
    ...item,
    wcRate: proposedRates[item.id]?.wcRate ?? (lookupWcRate(item.state, item.wcCode) ?? 1),
    sutaRate: proposedRates[item.id]?.sutaRate ?? getProposedSuta(item.state, item.sutaRate),
  })), [lineItems, proposedRates, getProposedSuta, lookupWcRate]);

  const currentTotals = useMemo(() => sumItems(lineItems, sutaBases), [lineItems, sutaBases]);
  const proposedTotals = useMemo(() => sumItems(proposedItems, sutaBases), [proposedItems, sutaBases]);
  
  const currentAdminFee = useMemo(() => 
    getAdminFee(adminFeeType, adminFeeValue, currentTotals.employees, currentTotals.grossWages),
    [getAdminFee, adminFeeType, adminFeeValue, currentTotals.employees, currentTotals.grossWages]
  );
  
  const proposedAdminFee = useMemo(() => 
    getAdminFee(proposedAdminFeeType, proposedAdminFeeValue, proposedTotals.employees, proposedTotals.grossWages),
    [getAdminFee, proposedAdminFeeType, proposedAdminFeeValue, proposedTotals.employees, proposedTotals.grossWages]
  );

  // Check if any line item has exceeded discount limits (based on user's discount level)
  const hasExceededDiscounts = useMemo(() => {
    // Admin level users (100% limit) have no restrictions
    if (discountLevel === 'admin') return false;
    
    return lineItems.some(item => {
      const standardWcRate = getStandardWcRate(item.state, item.wcCode);
      const standardSutaRate = getStandardSutaRate(item.state);
      const proposedWcRate = proposedRates[item.id]?.wcRate ?? standardWcRate ?? 0;
      const proposedSutaRate = proposedRates[item.id]?.sutaRate ?? standardSutaRate ?? item.sutaRate ?? 0;
      
      // Calculate discounts directly here to avoid stale closure issues
      const wcDiscount = standardWcRate ? calcDiscountPct(standardWcRate, proposedWcRate) : 0;
      const sutaDiscount = standardSutaRate ? calcDiscountPct(standardSutaRate, proposedSutaRate) : 0;
      
      const wcExceeded = standardWcRate && wcDiscount > MAX_DISCOUNT_PERCENT;
      const sutaExceeded = standardSutaRate && !isClientState(item.state) && sutaDiscount > MAX_DISCOUNT_PERCENT;
      
      return wcExceeded || sutaExceeded;
    });
  }, [discountLevel, lineItems, proposedRates, getStandardWcRate, getStandardSutaRate, calcDiscountPct, isClientState, MAX_DISCOUNT_PERCENT]);

  // Total service fees for unbundled ADP
  const totalServiceFees = useMemo(() => {
    if (!isAdp || !adpUnbundled) return 0;
    return lineItems.reduce((sum, item) => {
      const rate = serviceFees[item.id]?.rate || 0;
      return sum + (item.grossWages / 100) * rate;
    }, 0);
  }, [isAdp, adpUnbundled, lineItems, serviceFees]);
  
  const currentTotal = useMemo(() => 
    currentTotals.wcPremium + currentTotals.sutaTax + currentAdminFee + totalServiceFees,
    [currentTotals.wcPremium, currentTotals.sutaTax, currentAdminFee, totalServiceFees]
  );
  
  const proposedTotal = useMemo(() => 
    proposedTotals.wcPremium + proposedTotals.sutaTax + proposedAdminFee,
    [proposedTotals.wcPremium, proposedTotals.sutaTax, proposedAdminFee]
  );
  
  const savings = useMemo(() => currentTotal - proposedTotal, [currentTotal, proposedTotal]);
  const savingsPct = useMemo(() => 
    currentTotal > 0 ? ((savings / currentTotal) * 100).toFixed(1) : "0.0",
    [savings, currentTotal]
  );

  // ── PDF Download (using Web Worker) ──
  const downloadPDF = useCallback(() => {
    const exportData = {
      lineItems,
      proposedItems,
      sutaBases,
      sutaRates,
      currentTotals,
      proposedTotals,
      currentAdminFee,
      proposedAdminFee,
      currentTotal,
      proposedTotal,
      savings,
      savingsPct,
      isAdp,
      adpUnbundled,
      serviceFees,
      totalServiceFees,
      clientName,
      currentProvider,
      preparedBy,
      quoteDate,
      MONOPOLISTIC_STATES,
    };
    generatePdf(exportData);
  }, [lineItems, proposedItems, sutaBases, sutaRates, currentTotals, proposedTotals, currentAdminFee, proposedAdminFee, currentTotal, proposedTotal, savings, savingsPct, isAdp, adpUnbundled, serviceFees, totalServiceFees, clientName, currentProvider, preparedBy, quoteDate, MONOPOLISTIC_STATES, generatePdf]);

  // ── Excel Download (using optimized export) ──
  const downloadExcel = useCallback(() => {
    const exportData = {
      lineItems,
      proposedItems,
      sutaBases,
      sutaRates,
      WC_DESCRIPTIONS,
      MONOPOLISTIC_STATES,
      currentTotals,
      proposedTotals,
      currentAdminFee,
      proposedAdminFee,
      currentTotal,
      proposedTotal,
      savings,
      savingsPct,
      isAdp,
      adpUnbundled,
      serviceFees,
      totalServiceFees,
      clientName,
      currentProvider,
      preparedBy,
      quoteDate,
    };
    generateExcel(exportData);
  }, [lineItems, proposedItems, sutaBases, sutaRates, WC_DESCRIPTIONS, MONOPOLISTIC_STATES, currentTotals, proposedTotals, currentAdminFee, proposedAdminFee, currentTotal, proposedTotal, savings, savingsPct, isAdp, adpUnbundled, serviceFees, totalServiceFees, clientName, currentProvider, preparedBy, quoteDate, generateExcel]);

  // ── Word Download ──
  const downloadWord = useCallback(() => {
    const exportData = {
      lineItems,
      proposedItems,
      sutaBases,
      currentTotals,
      proposedTotals,
      currentAdminFee,
      proposedAdminFee,
      currentTotal,
      proposedTotal,
      savings,
      savingsPct,
      isAdp,
      adpUnbundled,
      totalServiceFees,
      clientName,
      currentProvider,
      preparedBy,
      quoteDate,
    };
    generateWord(exportData);
  }, [lineItems, proposedItems, sutaBases, currentTotals, proposedTotals, currentAdminFee, proposedAdminFee, currentTotal, proposedTotal, savings, savingsPct, isAdp, adpUnbundled, totalServiceFees, clientName, currentProvider, preparedBy, quoteDate, generateWord]);

  // Filtered WC rate table for admin (virtualized - no limit needed)
  const filteredWcRates = useMemo(() => wcRateTable.filter(r => {
    if (wcFilter.state && r.state !== wcFilter.state) return false;
    if (wcFilter.code && !r.classCode.includes(wcFilter.code)) return false;
    return true;
  }), [wcRateTable, wcFilter.state, wcFilter.code]);

  // ── Render ──
  if (dataLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-state">
        <div className="text-center">
          <AmplifyLogo size={48} className="mx-auto mb-4" />
          <Loader2 className="animate-spin mx-auto text-[#3c68ff] mb-3" size={28} />
          <p className="text-sm text-slate-500 font-medium">Loading rate tables...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pricing-engine">
      {/* Restore Draft Prompt */}
      {showRestorePrompt && (
        <RestoreDraftPrompt
          draft={savedDraft}
          onRestore={restoreDraft}
          onDismiss={dismissRestorePrompt}
        />
      )}

      {/* Top Navigation */}
      <Header
        user={user}
        isAdmin={isAdmin}
        logout={logout}
        page={page}
        setPage={setPage}
        saveStatus={saveStatus}
        saveQuote={saveQuote}
        saveAsNewVersion={saveAsNewVersion}
        saveAndNext={saveAndNext}
        onNavigateToDashboard={onNavigateToDashboard}
        newQuote={newQuote}
        lineItems={lineItems}
        hasExceededDiscounts={hasExceededDiscounts}
        currentQuoteId={currentQuoteId}
        setShowNewQuoteConfirm={setShowNewQuoteConfirm}
        hasUnsavedChanges={hasUnsavedChanges}
      />

      {/* Saved Quotes Drawer */}
      <SavedQuotesDrawer
        show={showSavedQuotes}
        onClose={() => setShowSavedQuotes(false)}
        quotes={savedQuotes}
        onLoad={loadQuote}
        onDelete={deleteQuote}
      />

      {/* New Quote Confirm Dialog */}
      <NewQuoteConfirmDialog
        show={showNewQuoteConfirm}
        onClose={() => setShowNewQuoteConfirm(false)}
        onConfirm={newQuote}
      />

      {/* Undo Banner */}
      <UndoBanner item={undoItem} onUndo={undoDelete} />

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-6 py-8">
        {/* ═══════════ PAGE 1: QUOTE BUILDER ═══════════ */}
        {page === 1 && (
          <div className="space-y-6" data-testid="quote-builder-page">
            {/* Client Info Row */}
            <ClientInfoCard
              clientName={clientName}
              setClientName={setClientName}
              currentProvider={currentProvider}
              setCurrentProvider={setCurrentProvider}
              preparedBy={preparedBy}
              setPreparedBy={setPreparedBy}
              quoteDate={quoteDate}
              setQuoteDate={setQuoteDate}
              isAdp={isAdp}
              adpUnbundled={adpUnbundled}
              setAdpUnbundled={setAdpUnbundled}
            />

            {/* Add Line Item Form */}
            <LineItemForm
              form={form}
              updateForm={updateForm}
              addLineItem={addLineItem}
              editingId={editingId}
              setEditingId={setEditingId}
              setForm={setForm}
              setTouched={setTouched}
              EMPTY_FORM={EMPTY_FORM}
              STATES={STATES}
              fieldInvalid={fieldInvalid}
              isAdp={isAdp}
              adpUnbundled={adpUnbundled}
              wcAutoFill={wcAutoFill}
              sutaAutoFill={sutaAutoFill}
              duplicateWarning={duplicateWarning}
              sutaBases={sutaBases}
              customSutaBases={customSutaBases}
            />

            {/* Line Items Table */}
            {lineItems.length > 0 && (
              <div className="space-y-4 mt-4">
                {/* Current Costs */}
                <CurrentCostsTable
                  lineItems={lineItems}
                  currentProvider={currentProvider}
                  isAdp={isAdp}
                  adpUnbundled={adpUnbundled}
                  adminFeeType={adminFeeType}
                  setAdminFeeType={setAdminFeeType}
                  adminFeeValue={adminFeeValue}
                  setAdminFeeValue={setAdminFeeValue}
                  serviceFees={serviceFees}
                  setServiceFees={setServiceFees}
                  currentTotals={currentTotals}
                  currentAdminFee={currentAdminFee}
                  currentTotal={currentTotal}
                  totalServiceFees={totalServiceFees}
                  editingSutaState={editingSutaState}
                  editingSutaValue={editingSutaValue}
                  setEditingSutaValue={setEditingSutaValue}
                  startEditingSuta={startEditingSuta}
                  updateCurrentStateSutaRate={updateCurrentStateSutaRate}
                  cancelEditingSuta={cancelEditingSuta}
                  startEditItem={startEditItem}
                  removeLineItem={removeLineItem}
                  calculate={calculate}
                  sutaBases={sutaBases}
                  WC_DESCRIPTIONS={WC_DESCRIPTIONS}
                />

                {/* Proposed Costs */}
                <ProposedCostsTable
                  lineItems={lineItems}
                  proposedItems={proposedItems}
                  proposedRates={proposedRates}
                  setProposedRates={setProposedRates}
                  proposedAdminFeeType={proposedAdminFeeType}
                  setProposedAdminFeeType={setProposedAdminFeeType}
                  proposedAdminFeeValue={proposedAdminFeeValue}
                  setProposedAdminFeeValue={setProposedAdminFeeValue}
                  proposedTotals={proposedTotals}
                  proposedAdminFee={proposedAdminFee}
                  proposedTotal={proposedTotal}
                  showAdminFeeWarning={showAdminFeeWarning}
                  setShowAdminFeeWarning={setShowAdminFeeWarning}
                  hasExceededDiscounts={hasExceededDiscounts}
                  MAX_DISCOUNT_PERCENT={MAX_DISCOUNT_PERCENT}
                  discountLevel={discountLevel}
                  isAdmin={isAdmin}
                  calculate={calculate}
                  sutaBases={sutaBases}
                  isClientState={isClientState}
                  MONOPOLISTIC_STATES={MONOPOLISTIC_STATES}
                  getStandardWcRate={getStandardWcRate}
                  getWcCostRate={getWcCostRate}
                  getStandardSutaRate={getStandardSutaRate}
                  getSutaCostRate={getSutaCostRate}
                  calcDiscountPct={calcDiscountPct}
                  updateStateSutaRate={updateStateSutaRate}
                />

                {/* Savings Summary & Navigate */}
                <SavingsSummary
                  savings={savings}
                  savingsPct={savingsPct}
                  showAdminFeeWarning={showAdminFeeWarning}
                  viewProposal={viewProposal}
                />
              </div>
            )}

            {/* Empty State */}
            {lineItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-20 text-slate-400" data-testid="empty-state">
                <Calculator size={48} strokeWidth={1} className="mb-4 text-slate-300" />
                <p className="text-lg font-medium text-slate-500">No line items yet</p>
                <p className="text-sm mt-1">Add your first line item above to build a quote</p>
              </div>
            )}
          </div>
        )}

        {/* ═══════════ PAGE 2: PROPOSAL ═══════════ */}
        {page === 2 && (
          <ProposalView
            setPage={setPage}
            exportProgress={exportProgress}
            printProposal={printProposal}
            downloadPDF={downloadPDF}
            downloadWord={downloadWord}
            downloadExcel={downloadExcel}
            isExporting={isExporting}
            quoteRef={quoteRef}
            clientName={clientName}
            lineItems={lineItems}
            preparedBy={preparedBy}
            quoteDate={quoteDate}
            savings={savings}
            savingsPct={savingsPct}
            currentProvider={currentProvider}
            currentTotals={currentTotals}
            proposedTotals={proposedTotals}
            currentAdminFee={currentAdminFee}
            proposedAdminFee={proposedAdminFee}
            currentTotal={currentTotal}
            proposedTotal={proposedTotal}
            isAdp={isAdp}
            adpUnbundled={adpUnbundled}
            totalServiceFees={totalServiceFees}
            serviceFees={serviceFees}
            proposedItems={proposedItems}
            sutaBases={sutaBases}
            isClientState={isClientState}
            MONOPOLISTIC_STATES={MONOPOLISTIC_STATES}
          />
        )}


        {/* ═══════════ PAGE 3: ADMIN ═══════════ */}
        {page === 3 && (
          <AdminPage
            STATES={STATES}
            DEFAULT_SUTA_BASES={DEFAULT_SUTA_BASES}
            customSutaBases={customSutaBases}
            setCustomSutaBases={setCustomSutaBases}
            sutaBases={sutaBases}
            sutaUploadInfo={sutaUploadInfo}
            sutaFileRef={sutaFileRef}
            handleSutaUpload={handleSutaUpload}
            adminApi={adminApi}
            sutaRates={sutaRates}
            sutaRatesDraft={sutaRatesDraft}
            sutaRatesSaved={sutaRatesSaved}
            startEditSutaRates={startEditSutaRates}
            resetSutaRatesToDefaults={resetSutaRatesToDefaults}
            saveSutaRates={saveSutaRates}
            cancelEditSutaRates={cancelEditSutaRates}
            updateDraftRate={updateDraftRate}
            wcRateTable={wcRateTable}
            setWcRateTable={setWcRateTable}
            wcUploadInfo={wcUploadInfo}
            wcFileRef={wcFileRef}
            handleWcUpload={handleWcUpload}
            wcFilter={wcFilter}
            setWcFilter={setWcFilter}
            filteredWcRates={filteredWcRates}
            wcIsCustom={wcIsCustom}
            flattenWcRates={flattenWcRates}
            DEFAULT_WC_RATES={DEFAULT_WC_RATES}
            WC_DESCRIPTIONS={WC_DESCRIPTIONS}
            WC_RISK_LABELS={WC_RISK_LABELS}
          />
        )}
      </main>
    </div>
  );
}
